import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import teamService from '@services/teamService';
import type { MyProfile, TeamMember } from '@/types/team';

/**
 * Perfil do usuário logado na equipe de marketing (GET /auth/profile), carregado uma vez após
 * o login. `is_manager` vem da hierarquia do cadastro (tem subordinados), não da role do Entra
 * ID — é o que liga os recursos de equipe (filtro de responsável, visão "Equipe", Cobertura).
 */
interface ProfileContextType {
  profile: MyProfile | null;
  isLoading: boolean;
  error: string | null;
  /** Gestor (pela hierarquia) ou admin: vê e delega as atividades da equipe. */
  canManageTeam: boolean;
  isAdmin: boolean;
  /** Cadastro do próprio usuário na equipe (pode não existir — ex.: admin fora do time). */
  self: TeamMember | null;
  /**
   * Pessoas a quem o usuário pode atribuir/filtrar: admin → toda a equipe ativa;
   * gestor → ele mesmo + subordinados; analista → só ele mesmo.
   */
  assignableMembers: TeamMember[];
  reload: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [allMembers, setAllMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const p = await teamService.getMyProfile();
      setProfile(p);
      if (p.is_admin) {
        try {
          setAllMembers(await teamService.getAll());
        } catch {
          setAllMembers([]);
        }
      } else {
        setAllMembers([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar o perfil');
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setAllMembers([]);
      setIsLoading(false);
      return;
    }
    load();
  }, [user, load]);

  const value = useMemo<ProfileContextType>(() => {
    const isAdmin = !!profile?.is_admin;
    const self = profile?.member ?? null;
    let assignable: TeamMember[];
    if (isAdmin && allMembers.length > 0) {
      assignable = allMembers.filter((m) => m.is_active);
    } else {
      assignable = [...(self ? [self] : []), ...(profile?.subordinates ?? [])];
    }
    const seen = new Set<number>();
    assignable = assignable
      .filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    return {
      profile,
      isLoading,
      error,
      canManageTeam: isAdmin || !!profile?.is_manager,
      isAdmin,
      self,
      assignableMembers: assignable,
      reload: load,
    };
  }, [profile, allMembers, isLoading, error, load]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProfile(): ProfileContextType {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile deve ser usado dentro de um ProfileProvider');
  }
  return context;
}
